import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { getRiskColor, getRiskIcon, calculateRefund, extractDeductionPercentage } from '../../utils/helpers';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import axios from 'axios';

var userEmail;
var JSToken;
var accessToken;
if (localStorage.user) {
  userEmail = localStorage.user_email;
  JSToken = JSON.parse(localStorage.user);
  accessToken = JSToken.access_token;
}
const config = {
  headers: { Authorization: `Bearer ${accessToken}` },
};


export const AdminActionCard = ({ requestId, requestData, onUpdate, deduction }) => {
  const [status, setStatus] = useState('Pending');
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionComment, setRejectionComment] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [refundAmount, setRefundAmount] = useState('0');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (requestData) {
      setStatus(requestData.status || 'Pending');
      setApprovalComment(requestData.approval_comment || '');
      setRejectionComment(requestData.rejection_comment || '');
      setVoucherCode(requestData.voucher_code || '');
      setPaidAmount(requestData.paid_amount || '');
    }
  }, [requestData]);

  useEffect(() => {
    if (paidAmount && deduction) {
      const deductionPercent = extractDeductionPercentage(deduction);
      const calculatedRefund = calculateRefund(paidAmount, deductionPercent);
      setRefundAmount(calculatedRefund);
    } else {
      setRefundAmount('0');
    }
  }, [paidAmount, deduction]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (status === 'Approved') {
      if (!paidAmount || parseFloat(paidAmount) <= 0) {
        toast.error('Please enter the Paid Amount before approving');
        return;
      }
      if (!refundAmount || parseFloat(refundAmount) < 0) {
        toast.error('Refund Amount must be calculated before approving');
        return;
      }
    }

    try {
      setLoading(true);
      const data = {
        approval_status: status,
        approval_comment: approvalComment,
        rejection_comment: rejectionComment,
        voucher_code: voucherCode,
        refund_amount: refundAmount,
        paid_amount: paidAmount
      };

        const response = await axios.put(
          `https://dash.bestdiplomats.org/api/resource/Conference Transfer Request/${requestId}`,
          data,
          config
        );
        if (response.status===200) {
        setSuccess(true);
        toast.success('Transfer request updated successfully');
        setTimeout(() => {
          setSuccess(false);
          if (onUpdate) onUpdate();
        }, 2000);
      } else {
        toast.error(response.error || 'Failed to update request');
      }
    } catch (error) {
      toast.error('Error updating request: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-4">
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
        <h3 className="text-xl font-bold text-white">Admin Decision</h3>
        <p className="text-indigo-100 text-sm">Review and update request status</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* Status Selection */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            Request Status *
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required
          >
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Appealed">Appealed</option>
            <option value="Transfered">Transfered</option>
            <option value="Blocked">Blocked</option>
          </select>
        </div>

        {/* Paid Amount */}
        <div>
          <label htmlFor="paid_amount" className="block text-sm font-medium text-gray-700 mb-2">
            Paid Amount {status === 'Approved' && <span className="text-red-600">*</span>}
          </label>
          <input
            type="number"
            id="paid_amount"
            step="0.01"
            value={paidAmount}
            onChange={(e) => setPaidAmount(e.target.value)}
            placeholder="Enter amount paid by user"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required={status === 'Approved'}
          />
          <p className="text-xs text-gray-500 mt-1">Amount originally paid for the source event</p>
        </div>

        {/* Refund Amount (Read-only) */}
        <div>
          <label htmlFor="refund_amount" className="block text-sm font-medium text-gray-700 mb-2">
            Refund Amount (Auto-calculated)
          </label>
          <input
            type="text"
            id="refund_amount"
            value={refundAmount}
            readOnly
            className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
          />
          {deduction && (
            <p className="text-xs text-gray-500 mt-1">
              After {deduction} deduction
            </p>
          )}
        </div>

        {/* Voucher Code */}
        <div>
          <label htmlFor="voucher_code" className="block text-sm font-medium text-gray-700 mb-2">
            Voucher Code
          </label>
          <input
            type="text"
            id="voucher_code"
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
            placeholder="Enter voucher code (optional)"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        {/* Approval Comment */}
        {status === 'Approved' && (
          <div>
            <label htmlFor="approval_comment" className="block text-sm font-medium text-gray-700 mb-2">
              Approval Comment
            </label>
            <textarea
              id="approval_comment"
              rows={3}
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              placeholder="Add notes about this approval..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
        )}

        {/* Rejection Comment */}
        {status === 'Rejected' && (
          <div>
            <label htmlFor="rejection_comment" className="block text-sm font-medium text-gray-700 mb-2">
              Rejection Comment *
            </label>
            <textarea
              id="rejection_comment"
              rows={3}
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              placeholder="Explain reason for rejection..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>
        )}

        {/* Submit Button */}
        <div className="pt-4">
          {success ? (
            <button
              type="button"
              className="w-full flex justify-center items-center gap-2 rounded-md bg-green-600 px-4 py-3 text-white font-semibold"
              disabled
            >
              <CheckCircleIcon className="h-5 w-5" />
              Updated Successfully
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center rounded-md bg-indigo-600 px-4 py-3 text-white font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Request'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};